// frontend/src/components/DocPreviewModal.jsx
// Reusable document preview modal
// PDF  → native <iframe> with signed URL (zero deps, works in all modern browsers)
// DOCX → fetched as ArrayBuffer, rendered via docx-preview

import { useEffect, useRef, useState } from "react";
import { X, Download, AlertTriangle, Loader2 } from "lucide-react";
import { renderAsync } from "docx-preview";
import { formatFileSize } from "./ui";

const PDF_MIME = "application/pdf";
const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// ─── DOCX renderer ─────────────────────────────────────────────────────────────
// streamUrl: backend proxy URL (same-origin, no CORS) — used when provided
// url: signed R2 URL fallback (may hit CORS if R2 CORS not configured)

function DocxViewer({ url, streamUrl }) {
  const containerRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchUrl = streamUrl || url;
    if (!fetchUrl || !containerRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        const token = localStorage.getItem("legal_token");
        const headers = {};
        if (streamUrl && token) headers["Authorization"] = `Bearer ${token}`;

        const res = await fetch(fetchUrl, { headers });
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const buffer = await res.arrayBuffer();
            if (cancelled) return;
        containerRef.current.innerHTML = "";
        await renderAsync(buffer, containerRef.current, undefined, {
          className: "docx-preview",
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          useBase64URL: true,
          renderChanges: false,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        });
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to render DOCX");
      }
    })();

    return () => { cancelled = true; };
  }, [url, streamUrl]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500">
        <AlertTriangle size={28} className="text-amber-400" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-auto bg-gray-100 p-4"
      style={{ minHeight: 0 }}
    />
  );
}

// ─── PDF renderer ──────────────────────────────────────────────────────────────

function PdfViewer({ url }) {
  return (
    <iframe
      src={url}
      title="PDF preview"
      className="w-full h-full border-0"
      style={{ minHeight: 0 }}
    />
  );
}

// ─── Main modal ────────────────────────────────────────────────────────────────
// Props:
//   getUrl     : async () => { url: string, fileName?: string }  — signed R2 URL (download + PDF iframe)
//   streamUrl? : string  — backend proxy path for DOCX fetch (avoids CORS); e.g. "/api/templates/:id/versions/:vid/stream"
//   fileName   : string
//   mimeType   : string
//   fileSize?  : number
//   onClose    : () => void

export default function DocPreviewModal({ getUrl, streamUrl, fileName, mimeType, fileSize, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isPdf = mimeType === PDF_MIME;
  const isDocx = mimeType === DOCX_MIME;
  const canPreview = isPdf || isDocx;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const result = await getUrl();
        if (!cancelled) setUrl(result.url ?? result);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load file");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  function handleDownload() {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.target = "_blank";
    a.click();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/60"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
        >
          <X size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
          {fileSize && (
            <p className="text-xs text-gray-400">{formatFileSize(fileSize)}</p>
          )}
        </div>
        <button
          onClick={handleDownload}
          disabled={!url}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors disabled:opacity-40"
        >
          <Download size={13} />
          Download
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full gap-3 text-white/80">
            <Loader2 size={22} className="animate-spin" />
            <span className="text-sm">Loading preview...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-white/80">
            <AlertTriangle size={28} className="text-amber-400" />
            <p className="text-sm">{error}</p>
            <button onClick={handleDownload} className="btn-primary text-sm mt-2">
              Download instead
            </button>
          </div>
        ) : !canPreview ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-white/80">
            <AlertTriangle size={28} className="text-amber-400" />
            <p className="text-sm">Preview not supported for this file type.</p>
            <button onClick={handleDownload} className="btn-primary text-sm mt-2">
              Download
            </button>
          </div>
        ) : isPdf ? (
          <PdfViewer url={url} />
        ) : (
          <DocxViewer url={url} streamUrl={streamUrl} />
        )}
      </div>
    </div>
  );
}
